import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { UserOctagon, Refresh2 } from "iconsax-react";
import { Button, Select } from "@/shared/ui";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { setPreviewRole, resetPreviewRole } from "@/features/auth";
import { HOME_PAGE_ROUTE } from "@/shared/utils";
import { PREVIEW_ROLE_OPTIONS, PREVIEW_ROLE_PERMISSIONS } from "../consts";

export default function RoleSwitcherRow() {
  const { t, i18n } = useTranslation("DemoPage");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const locale = (i18n.language as "ru" | "kk" | "en") || "ru";

  const currentRoleName = useAppSelector((state) => state.auth.data?.user?.roles?.[0]?.name ?? null);
  const originalRoleNames = useAppSelector((state) => state.auth.originalRoles?.map((role) => role.name) ?? []);
  const isPreviewing = currentRoleName !== null && !originalRoleNames.includes(currentRoleName);

  function labelFor(name: string): string {
    const option = PREVIEW_ROLE_OPTIONS.find((role) => role.name === name);
    if (!option) return name;
    if (locale === "kk") return option.display_name_kk;
    if (locale === "en") return option.display_name_en;
    return option.display_name_ru;
  }

  function handleSelect(value: string | null) {
    if (!value) return;
    const option = PREVIEW_ROLE_OPTIONS.find((role) => role.name === value);
    if (!option) return;
    dispatch(setPreviewRole({ role: option, permissions: PREVIEW_ROLE_PERMISSIONS[option.name] ?? [] }));
    navigate(HOME_PAGE_ROUTE);
  }

  function handleReset() {
    dispatch(resetPreviewRole());
    navigate(HOME_PAGE_ROUTE);
  }

  return (
    <div className="flex items-center gap-2 my-1 radius-sm py-1">
      <div className="flex items-center justify-center">
        <UserOctagon size={18} color="currentColor" className="shrink-0 content-base-primary" />
      </div>
      <div className="flex items-center justify-between flex-1 gap-2 min-w-0">
        <span className="text-label-sm content-base-primary whitespace-nowrap">{t("roleSwitcher.label")}</span>
        <div className="flex items-center gap-1.5 min-w-0">
          <Select<string>
            isSmall
            width="w-40"
            value={currentRoleName}
            onChange={handleSelect}
            options={PREVIEW_ROLE_OPTIONS.map((role) => ({ value: role.name, label: labelFor(role.name) }))}
          />
          {isPreviewing && (
            <Button
              variant="plain"
              isIconButton
              size="sm"
              type="button"
              title={t("roleSwitcher.reset")}
              aria-label={t("roleSwitcher.reset")}
              onClick={handleReset}
              className="shrink-0">
              <Refresh2 size={16} color="currentColor" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
